import { PrivateImage } from "@/components/PrivateMedia";
import { adminPhotoUrl } from "@/lib/adminNavigation";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["pending", "reviewing", "resolved", "dismissed"];
const ACTIONS = ["no_action", "warning", "suspend", "restore", "ban", "remove_content"];


function UserCard({ label, user, testId }) {
  const { t } = useI18n();
  if (!user) return null;
  return (
    <div className="nvl-card p-4" data-testid={testId}>
      <div className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {label}
      </div>
      <div className="flex items-center gap-3">
        {user.profile?.main_photo_url ? (
          <PrivateImage src={adminPhotoUrl(user.profile.main_photo_url)} alt=""
               className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-full"
               style={{ backgroundColor: "hsl(var(--muted))" }} />
        )}
        <div>
          <div className="font-medium">
            {user.profile?.display_name || user.email || "—"}
          </div>
          <div className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {user.email}
          </div>
          <div className="text-xs mt-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {user.account_status}
            {user.moderation_status && user.moderation_status !== "clean"
              ? ` · ${user.moderation_status}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}


export default function AdminReportDetail() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");
  const [action, setAction] = useState("");
  const [actionReason, setActionReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [retentionReason, setRetentionReason] = useState("");
  const [retentionSaving, setRetentionSaving] = useState(false);
  const retentionText = (it, en) => lang === "it" ? it : en;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/admin/reports/${reportId}`);
      setReport(r.data);
      setRetentionReason(r.data.retention?.reason || "");
      setNotes(r.data.admin_notes || "");
      setStatus(r.data.status || "pending");
      // A historical action is not a new command to execute on Save.
      setAction("");
      setActionReason("");
    } catch (e) {
      setError(extractError(e, t("app.admin.moderation.loadError")));
    } finally {
      setLoading(false);
    }
  }, [reportId, t]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        status: status || undefined,
        admin_notes: notes,
      };
      if (action) {
        body.action = action;
        body.action_reason = actionReason || null;
      }
      await api.patch(`/admin/reports/${reportId}`, body);
      toast.success(t("app.admin.moderation.detail.saved"));
      await load();
    } catch (e) {
      toast.error(extractError(e, t("app.admin.moderation.detail.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const reviewRetention = async (mode) => {
    setRetentionSaving(true);
    try {
      await api.patch(`/admin/reports/${reportId}/retention`, { mode, reason: retentionReason, expected_reviewed_at: report.reviewed_at || null, expected_retention_changed_at: report.retention?.changed_at || null });
      toast.success(retentionText("Conservazione aggiornata", "Retention updated"));
      await load();
    } catch (e) {
      toast.error(extractError(e, retentionText("Aggiornamento non riuscito. Ricarica la segnalazione.", "Update failed. Reload the report.")));
    } finally { setRetentionSaving(false); }
  };

  if (loading) {
    return (
      <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}
           data-testid="admin-report-detail-loading">
        {t("common.loading")}
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm" style={{ color: "hsl(var(--destructive))" }}
           data-testid="admin-report-detail-error">
        {error}
      </div>
    );
  }
  if (!report) return null;

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(lang === "it" ? "it-IT" : "en-GB"); }
    catch { return iso; }
  };

  return (
    <div data-testid="admin-report-detail-page">
      <button
        onClick={() => navigate("/admin/moderation")}
        className="flex items-center gap-2 text-sm mb-4"
        style={{ color: "hsl(var(--nvl-ink-soft))" }}
        data-testid="admin-report-detail-back"
      >
        <ArrowLeft size={14} /> {t("app.admin.nav.moderation")}
      </button>
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        #{report.id}
      </p>
      <h1 className="mt-2 text-3xl md:text-4xl">
        {t("app.admin.moderation.detail.title")}
      </h1>
      <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {fmt(report.created_at)}
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <UserCard label={t("app.admin.moderation.detail.reporter")}
                  user={report.reporter}
                  testId="admin-report-reporter" />
        <UserCard label={t("app.admin.moderation.detail.reported")}
                  user={report.reported}
                  testId="admin-report-reported" />
      </div>

      <div className="mt-6 nvl-card p-6 space-y-4">
        <div>
          <div className="nvl-overline mb-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.reason")}
          </div>
          <div className="font-medium">
            {t(`app.moderation.reasons.${report.reason}`, {}, report.reason)}
          </div>
        </div>
        {report.description ? (
          <div>
            <div className="nvl-overline mb-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.moderation.detail.description")}
            </div>
            <p className="whitespace-pre-wrap text-sm">{report.description}</p>
          </div>
        ) : null}
        {report.match_context ? (
          <div>
            <div className="nvl-overline mb-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.moderation.detail.matchContext")}
            </div>
            <p className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              #{report.match_context.match_id} · {report.match_context.status}
            </p>
          </div>
        ) : null}
        {report.message_context ? (
          <div className="nvl-card p-4" style={{ backgroundColor: "hsl(var(--muted))" }}>
            <div className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.moderation.detail.messageContext")}
            </div>
            {report.message_context.body ? (
              <p className="whitespace-pre-wrap text-sm">{report.message_context.body}</p>
            ) : null}
            {report.message_context.attachment_kind ? (
              <div className="text-xs mt-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {report.message_context.attachment_kind}
                {report.message_context.attachment_url ? (
                  <>{" "}
                    <a href={report.message_context.attachment_url}
                       target="_blank" rel="noreferrer" className="underline">
                      open
                    </a>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.noMessageContext")}
          </div>
        )}
      </div>

      <section className="mt-6 nvl-card p-6 space-y-3" data-testid="report-retention">
        <h2 className="text-xl">{retentionText("Conservazione della segnalazione", "Report retention")}</h2>
        <p className="text-sm">{retentionText(
          "Conferma la chiusura solo dopo aver verificato che non ci siano contestazioni o obblighi di conservazione. La segnalazione e il relativo registro di moderazione saranno rimossi dopo 12 mesi. Una modifica alla pratica richiede una nuova conferma.",
          "Confirm closure only after checking there are no disputes or retention obligations. The report and its moderation log will be removed after 12 months. Editing the case requires a new confirmation.")}</p>
        <p className="text-sm" role="status">{report.retention?.hold
          ? retentionText("Cancellazione sospesa. Riesame previsto: ", "Deletion on hold. Review due: ") + fmt(report.retention.review_at)
          : report.retention?.expires_at
            ? retentionText("Rimozione a partire dal: ", "Removal from: ") + fmt(report.retention.expires_at)
            : retentionText("Nessuna rimozione programmata: chiusura da confermare.", "No removal scheduled: closure needs confirmation.")}</p>
        <button type="button" className="nvl-btn nvl-btn-secondary" disabled={saving || retentionSaving || !["resolved", "dismissed"].includes(report.status)}
          onClick={() => reviewRetention("close")}>
          {retentionText("Conferma chiusura senza controversie", "Confirm closure without disputes")}
        </button>
        <label className="block text-sm">
          {retentionText("Motivo per sospendere la cancellazione (senza dettagli sensibili)", "Reason to hold deletion (no sensitive details)")}
          <input className="nvl-input mt-2" value={retentionReason} maxLength={500}
            onChange={(e) => setRetentionReason(e.target.value)} />
        </label>
        <button type="button" className="nvl-btn nvl-btn-secondary" disabled={saving || retentionSaving || !retentionReason.trim()}
          onClick={() => reviewRetention("hold")}>
          {retentionText("Sospendi e riesamina tra 30 giorni", "Hold and review in 30 days")}
        </button>
        <p className="text-xs">{retentionText("La sospensione non scade automaticamente. Il riesame va gestito dall’amministratore.", "A hold does not expire automatically. The administrator must review it.")}</p>
      </section>

      <div className="mt-6 nvl-card p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.statusLabel")}
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="nvl-input mt-2"
            data-testid="admin-report-status"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`app.admin.moderation.status.${s}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          {report.action ? (
            <div className="mb-4 text-sm" data-testid="admin-report-previous-action">
              <p>{retentionText("Ultima azione registrata: ", "Last recorded action: ")}{t(`app.admin.moderation.action.${report.action}`)}</p>
              {report.action_reason ? <p className="mt-1 whitespace-pre-wrap">{report.action_reason}</p> : null}
            </div>
          ) : null}
          <p className="mb-2 text-xs">{retentionText(
            "Seleziona un’azione solo per eseguirla adesso. Salvare note o stato non ripete le azioni precedenti.",
            "Select an action only to perform it now. Saving notes or status does not repeat earlier actions.")}</p>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.actionLabel")}
          </label>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="nvl-input mt-2"
            data-testid="admin-report-action"
          >
            <option value="">{t("app.admin.moderation.detail.noAction")}</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {t(`app.admin.moderation.action.${a}`)}
              </option>
            ))}
          </select>
        </div>

        {action ? (
          <div>
            <label className="text-xs uppercase tracking-widest"
                   style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.moderation.detail.actionReasonLabel")}
            </label>
            <input
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              className="nvl-input mt-2"
              data-testid="admin-report-action-reason"
              maxLength={500}
            />
          </div>
        ) : null}

        <div>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.adminNotesLabel")}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="nvl-input mt-2 min-h-[120px]"
            placeholder={t("app.admin.moderation.detail.adminNotesPlaceholder")}
            data-testid="admin-report-notes"
            maxLength={4000}
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="nvl-btn nvl-btn-primary disabled:opacity-50"
            data-testid="admin-report-save"
          >
            {saving ? t("common.loading") : t("app.admin.moderation.detail.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
